import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';

const bookingStatusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' }
];

const paymentStatusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'refunded', label: 'Refunded' }
];

const formatAmount = (value) => {
  const amount = Number(value || 0);
  return amount.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
};

const formatDate = (value) => {
  if (!value) return '--';
  return new Date(value).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

const titleCase = (value) => {
  if (!value) return '--';
  return value.charAt(0).toUpperCase() + value.slice(1);
};

const getPaidAmount = (booking) => (
  Array.isArray(booking?.payments)
    ? booking.payments.reduce((sum, payment) => sum + Number(payment.amount || 0), 0)
    : 0
);

const statusBadgeClass = (status, type = 'booking') => {
  const value = status || 'pending';
  const classes = {
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    partial: 'bg-blue-50 text-blue-700 border-blue-200',
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    refunded: 'bg-slate-100 text-slate-700 border-slate-200',
    confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
    completed: 'bg-indigo-50 text-indigo-700 border-indigo-200'
  };

  return `inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${classes[value] || classes.pending}`;
};

const BookingList = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [bookingStatus, setBookingStatus] = useState('All');
  const [paymentStatus, setPaymentStatus] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    transactionId: '',
    paymentMode: 'UPI',
    paidAt: '',
    remarks: ''
  });

  useEffect(() => {
    fetchBookings();
  }, [currentPage, recordsPerPage, keyword, bookingStatus, paymentStatus]);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const response = await apiService.post('bookingList', {
        start: (currentPage - 1) * recordsPerPage,
        length: recordsPerPage,
        search: keyword,
        bookingStatus,
        paymentStatus
      });

      setBookings(response.data || []);
      setTotalRecords(response.recordsFiltered || 0);
    } catch (error) {
      console.error('Booking fetch error:', error);
      toast.error('Failed to load booking list');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentStatusChange = async (id, value) => {
    try {
      const response = await apiService.put(`bookingPaymentStatus/${id}`, { paymentStatus: value });
      if (response.status) {
        setBookings((prev) => prev.map((item) => item._id === id ? { ...item, paymentStatus: value } : item));
        toast.success(response.message || 'Payment status updated successfully');
      } else {
        toast.error(response.message || 'Failed to update payment status');
      }
    } catch (error) {
      console.error('Payment status update error:', error);
      toast.error('Something went wrong');
    }
  };

  const handleBookingStatusChange = async (id, value) => {
    try {
      const response = await apiService.put(`bookingStatus/${id}`, { bookingStatus: value });
      if (response.status) {
        setBookings((prev) => prev.map((item) => item._id === id ? { ...item, bookingStatus: value } : item));
        toast.success(response.message || 'Booking status updated successfully');
      } else {
        toast.error(response.message || 'Failed to update booking status');
      }
    } catch (error) {
      console.error('Booking status update error:', error);
      toast.error('Something went wrong');
    }
  };

  const openPaymentModal = (booking) => {
    setSelectedBooking(booking);
    setPaymentForm({
      amount: '',
      transactionId: '',
      paymentMode: 'UPI',
      paidAt: '',
      remarks: ''
    });
    setShowPaymentModal(true);
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedBooking(null);
  };

  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    setPaymentForm((prev) => ({ ...prev, [name]: value }));
  };

  const submitPayment = async (e) => {
    e.preventDefault();
    if (!selectedBooking?._id) return;

    try {
      setPaymentSubmitting(true);
      const response = await apiService.post(`bookingPayment/${selectedBooking._id}`, {
        amount: Number(paymentForm.amount || 0),
        transactionId: paymentForm.transactionId,
        paymentMode: paymentForm.paymentMode,
        paidAt: paymentForm.paidAt,
        remarks: paymentForm.remarks
      });

      if (response.status) {
        toast.success(response.message || 'Payment added successfully');
        setBookings((prev) => prev.map((item) => item._id === selectedBooking._id ? response.data : item));
        closePaymentModal();
      } else {
        toast.error(response.message || 'Failed to add payment');
      }
    } catch (error) {
      console.error('Add payment error:', error);
      toast.error('Something went wrong');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const totalPages = Math.ceil(totalRecords / recordsPerPage);
  const startRecord = totalRecords === 0 ? 0 : (currentPage - 1) * recordsPerPage + 1;
  const endRecord = Math.min(currentPage * recordsPerPage, totalRecords);

  return (
    <div className="p-0">
      <div className="mb-3">
        <nav className="flex flex-wrap items-center text-xs sm:text-sm">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 no-underline">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Booking Manage</span>
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-4 sm:p-6 border-b flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-1 flex-col sm:flex-row gap-3">
            <div className="relative flex-1 min-w-[220px]">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="Search by traveller, invoice, phone, email, package, or location"
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <select
              value={bookingStatus}
              onChange={(e) => {
                setBookingStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="All">Booking: All</option>
              {bookingStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <select
              value={paymentStatus}
              onChange={(e) => {
                setPaymentStatus(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="All">Payment: All</option>
              {paymentStatusOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={() => navigate('/add-booking')}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg"
            style={{ backgroundColor: '#009ef7' }}
          >
            <i className="bi bi-plus-lg"></i>
            Add Booking
          </button>
        </div>

        <div className="p-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#009ef7' }}></div>
              <span className="ml-2 text-gray-600">Loading...</span>
            </div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12">
              <i className="bi bi-journal-check text-4xl text-gray-300 mb-2"></i>
              <h3 className="text-lg font-medium text-gray-900 mb-1">No Bookings Found</h3>
              <p className="text-gray-500">Create your first booking to get started.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="w-full min-w-[1120px] text-[13px]">
                  <thead className="bg-slate-100">
                    <tr className="text-left text-slate-600 text-[11px] uppercase tracking-wide">
                      <th className="px-3 py-3 w-12">#</th>
                      <th className="px-3 py-3 min-w-[170px]">Booking</th>
                      <th className="px-3 py-3 min-w-[220px]">Traveller</th>
                      <th className="px-3 py-3 min-w-[220px]">Trip</th>
                      <th className="px-3 py-3 min-w-[170px]">Financials</th>
                      <th className="px-3 py-3 min-w-[190px]">Status</th>
                      <th className="px-3 py-3 min-w-[150px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {bookings.map((item, index) => {
                      const totalAmount = Number(item.packageDetail?.totalAmount || item.packageDetail?.amount || 0);
                      const paidAmount = getPaidAmount(item);
                      const dueAmount = Math.max(totalAmount - paidAmount, 0);

                      return (
                        <tr key={item._id} className="hover:bg-slate-50 align-top">
                          <td className="px-3 py-3 text-slate-500">{startRecord + index}</td>
                          <td className="px-3 py-3">
                            <button
                              type="button"
                              onClick={() => navigate(`/booking-detail/${item._id}`)}
                              className="font-semibold text-[#009ef7] hover:underline"
                            >
                              {item.invoiceNumber || '--'}
                            </button>
                            <div className="mt-1 text-xs text-slate-500">Created {formatDate(item.createdAt)}</div>
                            <div className="mt-2 text-[11px] text-slate-500">{item.payments?.length || 0} payment(s)</div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="font-semibold text-slate-900">{item.travellerName || '--'}</div>
                            <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-600">
                              <i className="bi bi-telephone text-slate-400"></i>
                              {item.phone || '--'}
                            </div>
                            <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-600 break-all">
                              <i className="bi bi-envelope text-slate-400"></i>
                              {item.email || '--'}
                            </div>
                            <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                              <i className="bi bi-geo-alt text-slate-400"></i>
                              {item.location || '--'}
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="font-medium text-slate-900">{item.packageDetail?.name || '--'}</div>
                            <div className="mt-1 text-xs text-slate-600">{item.packageDetail?.duration || '--'}</div>
                            <div className="mt-1 text-xs text-slate-500">
                              {item.travelStartDate ? new Date(item.travelStartDate).toLocaleDateString('en-IN') : '--'}
                              {' '}to{' '}
                              {item.travelEndDate ? new Date(item.travelEndDate).toLocaleDateString('en-IN') : '--'}
                            </div>
                            <div className="mt-2">
                              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-700">
                                {titleCase(item.tourType)}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="text-sm font-semibold text-slate-900">{formatAmount(totalAmount)}</div>
                            <div className="mt-1 grid grid-cols-[42px_1fr] gap-x-1 text-xs">
                              <span className="text-slate-500">Paid</span>
                              <span className="font-medium text-emerald-700">{formatAmount(paidAmount)}</span>
                              <span className="text-slate-500">Due</span>
                              <span className="font-medium text-rose-700">{formatAmount(dueAmount)}</span>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="space-y-2">
                              <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                  <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Payment</span>
                                  <span className={statusBadgeClass(item.paymentStatus, 'payment')}>{titleCase(item.paymentStatus)}</span>
                                </div>
                                <select
                                  value={item.paymentStatus || 'pending'}
                                  onChange={(e) => handlePaymentStatusChange(item._id, e.target.value)}
                                  className="h-6 w-full rounded border border-slate-300 bg-white px-1.5 text-[10px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  {paymentStatusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <div className="flex items-center gap-1.5 mb-1">
                                  <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Booking</span>
                                  <span className={statusBadgeClass(item.bookingStatus)}>{titleCase(item.bookingStatus)}</span>
                                </div>
                                <select
                                  value={item.bookingStatus || 'pending'}
                                  onChange={(e) => handleBookingStatusChange(item._id, e.target.value)}
                                  className="h-6 w-full rounded border border-slate-300 bg-white px-1.5 text-[10px] text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                  {bookingStatusOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-3">
                            <div className="flex flex-col gap-2">
                              <button
                                type="button"
                                onClick={() => navigate(`/booking-detail/${item._id}`)}
                                className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md text-xs font-semibold text-[#009ef7] bg-[#e8f4fd] hover:bg-[#d9effd]"
                              >
                                <i className="bi bi-eye"></i>
                                Details
                              </button>
                              <button
                                type="button"
                                onClick={() => openPaymentModal(item)}
                                className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md text-xs font-semibold text-white"
                                style={{ backgroundColor: '#009ef7' }}
                              >
                                <i className="bi bi-plus-circle"></i>
                                Payment
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 mt-4">
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-gray-700">Show</span>
                  <select
                    value={recordsPerPage}
                    onChange={(e) => {
                      setRecordsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="px-3 py-1 border border-gray-300 rounded-lg text-xs sm:text-sm bg-white text-gray-700"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-xs sm:text-sm text-gray-700">entries</span>
                  <span className="text-xs sm:text-sm text-gray-500 hidden md:inline">({startRecord} to {endRecord} of {totalRecords})</span>
                </div>

                <div className="flex items-center gap-1 flex-wrap justify-center">
                  <button onClick={() => setCurrentPage(1)} disabled={currentPage === 1} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">&laquo;</button>
                  {[...Array(totalPages)].map((_, i) => {
                    const page = i + 1;
                    if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                      return (
                        <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 border rounded text-xs sm:text-sm ${currentPage === page ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-gray-300 hover:bg-gray-100'}`}>
                          {page}
                        </button>
                      );
                    }
                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-2 text-gray-500">...</span>;
                    }
                    return null;
                  })}
                  <button onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages || totalPages === 0} className="px-3 py-1 border border-gray-300 rounded text-xs sm:text-sm disabled:opacity-50 hover:bg-gray-100">&raquo;</button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {showPaymentModal && selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <form onSubmit={submitPayment} className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900 m-0">Add Payment</h3>
                <p className="text-xs text-gray-500 m-0">Invoice: {selectedBooking.invoiceNumber}</p>
              </div>
              <button type="button" onClick={closePaymentModal} className="text-gray-400 hover:text-gray-700">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1.5">Paid Amount</label>
                <input
                  type="number"
                  min="1"
                  value={paymentForm.amount}
                  name="amount"
                  onChange={handlePaymentFormChange}
                  className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1.5">Payment Mode</label>
                <select name="paymentMode" value={paymentForm.paymentMode} onChange={handlePaymentFormChange} className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm bg-white">
                  <option value="UPI">UPI</option>
                  <option value="Bank Transfer">Bank Transfer</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1.5">Transaction ID</label>
                <input name="transactionId" value={paymentForm.transactionId} onChange={handlePaymentFormChange} className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm" />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1.5">Payment Date Time</label>
                <input type="datetime-local" name="paidAt" value={paymentForm.paidAt} onChange={handlePaymentFormChange} className="w-full h-10 px-3 border border-gray-300 rounded-md text-sm" />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-xs font-semibold uppercase text-gray-500 mb-1.5">Payment Remarks</label>
                <textarea name="remarks" value={paymentForm.remarks} onChange={handlePaymentFormChange} rows="3" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm" />
              </div>
            </div>

            <div className="px-5 py-4 border-t bg-gray-50 flex justify-end gap-2">
              <button type="button" onClick={closePaymentModal} disabled={paymentSubmitting} className="h-10 px-4 text-sm font-medium rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300">
                Cancel
              </button>
              <button type="submit" disabled={paymentSubmitting} className="h-10 px-4 text-sm font-semibold rounded-md text-white disabled:opacity-50 inline-flex items-center gap-2" style={{ backgroundColor: '#009ef7' }}>
                {paymentSubmitting && <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>}
                {paymentSubmitting ? 'Please wait...' : 'Save Payment'}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default BookingList;
